import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Container,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { format, isValid } from 'date-fns';
import { downloadAccountsReport } from '../../../services/api';

const AccountsDownload = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  // Validate dates
  const isValidDateRange = () => {
    if (!fromDate || !toDate) return true; // Allow empty dates
    return isValid(fromDate) && isValid(toDate) && fromDate <= toDate;
  };

  // Format date for API
  const formatDateForAPI = (date) => {
    if (!date || !isValid(date)) return '';
    return format(date, 'MM/dd/yyyy');
  };

  // Reset form
  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setMessage({ type: '', text: '' });
  };

  // Generic download handler
  const handleDownload = async (reportType, filename) => {
    if (!isValidDateRange()) {
      setMessage({
        type: 'error',
        text: 'Please select a valid date range'
      });
      return;
    }

    setLoading({ ...loading, [reportType]: true });
    setMessage({ type: '', text: '' });

    try {
      const params = {
        fromDate: formatDateForAPI(fromDate),
        toDate: formatDateForAPI(toDate),
        reportType
      };

      const response = await downloadAccountsReport(params);
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage({
        type: 'success',
        text: `${filename} downloaded successfully`
      });
    } catch (error) {
      console.error('Download error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Download failed. Please try again.'
      });
    } finally {
      setLoading({ ...loading, [reportType]: false });
    }
  };

  // Download handlers for different report types
  const handleAssessmentReportDownload = () => {
    handleDownload('assessment', 'AssessmentReport.xlsx');
  };

  const handleQMSReportDownload = () => {
    handleDownload('qms', 'QMSReport.xlsx');
  };

  const handlePaymentReportDownload = () => {
    handleDownload('payment', 'PaymentReport.xlsx');
  };

  const handleTestingAAFEEDownload = () => {
    handleDownload('testing_aafee', 'TestingAAFEE.xlsx');
  };

  const handleCalibrationAAFEEDownload = () => {
    handleDownload('calibration_aafee', 'CalibrationAAFEE.xlsx');
  };

  const handleMedicalAAFEEDownload = () => {
    handleDownload('medical_aafee', 'MedicalAAFEE.xlsx');
  };

  const handlePTProviderAAFEEDownload = () => {
    handleDownload('pt_provider_aafee', 'PTProviderAAFEE.xlsx');
  };

  const handleRMProducerAAFEEDownload = () => {
    handleDownload('rm_producer_aafee', 'RMProducerAAFEE.xlsx');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Data Download
          </Typography>
          
          <Divider sx={{ mb: 3 }} />

          {/* Date Range Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Date Range
              </Typography>
              
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={setFromDate}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: fromDate && !isValid(fromDate)
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={setToDate}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: toDate && !isValid(toDate)
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RefreshIcon />}
                    fullWidth
                  >
                    Reset
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Report Downloads */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Report Downloads
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAssessmentReportDownload}
                    startIcon={loading.assessment ? <CircularProgress size={20} /> : <AssessmentIcon />}
                    disabled={loading.assessment}
                    fullWidth
                    sx={{ height: 48 }}
                  >
                    Download Assessment Report
                  </Button>
                </Grid>
                
                {/* <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleQMSReportDownload}
                    startIcon={loading.qms ? <CircularProgress size={20} /> : <BusinessIcon />}
                    disabled={loading.qms}
                    fullWidth
                    sx={{ height: 48 }}
                  >
                    Download QMS Report
                  </Button>
                </Grid> */}
                
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePaymentReportDownload}
                    startIcon={loading.payment ? <CircularProgress size={20} /> : <PaymentIcon />}
                    disabled={loading.payment}
                    fullWidth
                    sx={{ height: 48 }}
                  >
                    Download Payment Report
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* AAFEE Downloads */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                AAFEE Reports
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleTestingAAFEEDownload}
                    startIcon={loading.testing_aafee ? <CircularProgress size={20} /> : <DownloadIcon />}
                    disabled='true'
                    fullWidth
                    sx={{ height: 48 }}
                  >
                    Testing (AAFEE)
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleCalibrationAAFEEDownload}
                    startIcon={loading.calibration_aafee ? <CircularProgress size={20} /> : <DownloadIcon />}
                    disabled='true'
                    fullWidth
                    sx={{ height: 48 }}
                  >
                    Calibration (AAFEE)
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleMedicalAAFEEDownload}
                    startIcon={loading.medical_aafee ? <CircularProgress size={20} /> : <DownloadIcon />}
                    disabled={loading.medical_aafee}
                    fullWidth
                    sx={{ height: 48 }}
                  >
                    Medical (AAFEE)
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handlePTProviderAAFEEDownload}
                    startIcon={loading.pt_provider_aafee ? <CircularProgress size={20} /> : <DownloadIcon />}
                    disabled='true'
                    fullWidth
                    sx={{ height: 48 }}
                  >
                    Proficiency Testing Provider (AAFEE)
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleRMProducerAAFEEDownload}
                    startIcon={loading.rm_producer_aafee ? <CircularProgress size={20} /> : <DownloadIcon />}
                    disabled='true'
                    fullWidth
                    sx={{ height: 48 }}
                  >
                    Reference Material Producers (AAFEE)
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Message Display */}
          {message.text && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={message.type}>
                {message.text}
              </Alert>
            </Box>
          )}
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default AccountsDownload;